// src/components/PixPaymentModal.js
import React, { useState, useEffect } from 'react';
import Modal from './Modal.js';
import pixPaymentService from '../services/pixPaymentService.js';
import essayService from '../services/essayService.js';
import '../styles/PixPaymentModal.css';
import { useTranslation } from 'react-i18next';

const PixPaymentModal = ({ isOpen, onClose, onPaymentSuccess, user }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('PENDING');
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  // Reset ao abrir/fechar
  useEffect(() => {
    if (!isOpen) {
      setPaymentData(null);
      setError(null);
      setCheckingStatus(false);
      setPaymentStatus('PENDING');
      setCopied(false);
      setUrlCopied(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !user) return;

    const check = async () => {
      try {
        await checkUserPaymentStatus(user.uid);  // your API call
      } catch (err) {
        console.error("Payment status check failed:", err);
      }
    };

    // Run immediately when modal opens
    check();

    // Re-run when user returns to the page / regains connection

    const handleFocus = () => check();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    const handleOnline = () => check();
    const handlePageShow = () => check(); // bfcache restore on iOS/Safari

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [isOpen, user]);


  const handleCreatePayment = async () => {
    setLoading(true);
    setError(null);

    try {
      const payment = await pixPaymentService.createPixPayment(
        user.uid,
        user.email,
        user.displayName || user.name,
        user.phoneNumber || null,
        "11578623731" // taxId - você pode coletar isso do usuário ou usar um placeholder
      );
      
      console.log('Payment created:', {
        id: payment.id,
        externalId: payment.externalId,
        hasCopyPaste: !!payment.copyAndPaste,
        hasQrCodeBase64: !!payment.qrCodeBase64,
        hasQrCodeImage: !!payment.qrCodeImage, // Added this field
        hasPaymentUrl: !!payment.paymentUrl,
        method: payment.method,
        rawData: payment // Log full payment data for debugging
      });
      
      setPaymentData(payment);
      
      // Verificar se temos dados PIX necessários
      const hasQrCode = payment.qrCodeBase64 || payment.qrCodeImage || payment.qrCodeDataUrl;
      const hasCopyPaste = payment.copyAndPaste;
      
      if (!hasQrCode && !hasCopyPaste && payment.paymentUrl) {
        // Se não temos PIX direto mas temos URL de pagamento
        setError('PIX QR Code não disponível. Use o link de pagamento para prosseguir.');
      } else if (!hasQrCode && !hasCopyPaste && !payment.paymentUrl) {
        // Se não temos nenhum método de pagamento
        setError('Erro: nenhum método de pagamento disponível. Tente novamente.');
      } else {
        // Temos pelo menos um método - iniciar checagem
        startPaymentStatusCheck(payment.id, payment.method);
      }
    } catch (err) {
      setError(err.message || 'Erro ao criar pagamento. Por favor, tente novamente.');
      console.error('Payment creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Checagem periódica (usa checkPaymentStatus(localPaymentId))
  const startPaymentStatusCheck = (paymentId, method = 'billing') => {
    setCheckingStatus(true);
    let checkCount = 0;
    const maxChecks = 120; // ~10min (120*5s)

    const checkInterval = setInterval(async () => {
      checkCount++;
      try {
        const statusObj = await pixPaymentService.checkPaymentStatus(paymentId, method);
        const st = statusObj?.status || 'PENDING';

        console.log(`Payment status check ${checkCount}:`, { paymentId, status: st });

        if (st === 'PAID' || st === 'COMPLETED') {
          clearInterval(checkInterval);
          setCheckingStatus(false);
          setPaymentStatus('PAID');
          
          // Add credits to user account
          try {
            await essayService.addCredits(user.uid, 2);
            console.log('Added 2 credits to user after successful payment');
          } catch (err) {
            console.error('Failed to add credits after payment:', err);
          }
          
          setTimeout(() => {
            onPaymentSuccess && onPaymentSuccess();
            onClose && onClose();
          }, 1500);
        } else if (['EXPIRED', 'CANCELLED', 'REFUNDED'].includes(st)) {
          clearInterval(checkInterval);
          setCheckingStatus(false);
          setPaymentStatus(st);
          setError('Pagamento não concluído. Gere um novo PIX.');
        } else if (checkCount >= maxChecks) {
          clearInterval(checkInterval);
          setCheckingStatus(false);
          setError('Tempo de verificação expirado. Verifique manualmente ou tente novamente.');
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        // mantém o loop; se erro persistir o timer vai expirar
      }
    }, 5000);

    // cleanup on unmount
    return () => clearInterval(checkInterval);
  };

  const handleCopyPixKey = () => {
    // Usar copyAndPaste retornado pela AbacatePay
    const code = paymentData?.copyAndPaste;
    if (code) {
      navigator.clipboard
        .writeText(code)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        })
        .catch((err) => console.error('Failed to copy PIX code:', err));
    }
  };

  const handleCopyPaymentUrl = () => {
    // Copiar URL de pagamento se disponível
    const url = paymentData?.paymentUrl;
    if (url) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setUrlCopied(true);
          setTimeout(() => setUrlCopied(false), 2500);
        })
        .catch((err) => console.error('Failed to copy payment URL:', err));
    }
  };

  const handleOpenPaymentLink = () => {
    // Usar paymentUrl se disponível
    if (paymentData?.paymentUrl) {
      window.open(paymentData.paymentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleManualCheck = async () => {
    if (!paymentData?.id) return;
    
    setCheckingStatus(true);
    try {
      const statusObj = await pixPaymentService.checkPaymentStatus(
        paymentData.id, 
        paymentData.method || 'billing'
      );
      const st = statusObj?.status || 'PENDING';
      
      if (st === 'PAID' || st === 'COMPLETED') {
        setPaymentStatus('PAID');
        
        // Add credits to user account
        try {
          await essayService.addCredits(user.uid, 2);
          console.log('Added 2 credits to user after manual payment check');
        } catch (err) {
          console.error('Failed to add credits after manual payment check:', err);
        }
        
        setTimeout(() => {
          onPaymentSuccess && onPaymentSuccess();
          onClose && onClose();
        }, 1500);
      } else if (['EXPIRED', 'CANCELLED', 'REFUNDED'].includes(st)) {
        setPaymentStatus(st);
        setError('Pagamento não foi encontrado como pago. Tente novamente.');
      } else {
        setError('Pagamento ainda está pendente. Aguarde ou tente novamente em alguns minutos.');
      }
    } catch (err) {
      setError('Erro ao verificar status do pagamento.');
    } finally {
      setCheckingStatus(false);
    }
  };

  // Helper function to get QR code image source
  const getQrCodeImageSrc = () => {
    if (!paymentData) return null;

    // Try different possible field names for QR code image
    const qrCodeImage = paymentData.qrCodeImage || 
                       paymentData.qrCodeBase64 || 
                       paymentData.qrCodeDataUrl ||
                       paymentData.qrcode ||
                       paymentData.qrCode; // Try different variations

    if (!qrCodeImage) return null;

    // If it's already a data URL, return as is
    if (qrCodeImage.startsWith('data:')) {
      return qrCodeImage;
    }

    // If it's a URL, return as is
    if (qrCodeImage.startsWith('http')) {
      return qrCodeImage;
    }

    // If it's base64 without data URL prefix, add it
    if (qrCodeImage.length > 50) { // Assume it's base64 if long enough
      return `data:image/png;base64,${qrCodeImage}`;
    }

    return null;
  };

  // Helper function to check if we have any QR code data
  const hasQrCodeData = () => {
    return !!getQrCodeImageSrc();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="pix-payment-modal">
        <div className="modal-header">
          <h2>💳 Pagamento via PIX</h2>
          <button className="close-btn" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className="payment-info">
          <p className="payment-description">
            Para acessar a análise completa de até 20 redações com inteligência artificial,
            realize o pagamento único de:
          </p>
          <div className="payment-amount">
            <span className="currency">R$</span>
            <span className="value">12,00</span>
          </div>
          <p className="payment-note">
            ✅ Análise detalhada das 5 competências do ENEM<br />
            ✅ Correções e sugestões de melhoria<br />
            ✅ Nota estimada de 0 a 1000 pontos<br />
            ✅ Rastreamento de progresso
          </p>
        </div>

        {!paymentData && !loading && (
          <div className="payment-actions">
            <button
              className="btn-create-payment"
              onClick={handleCreatePayment}
              disabled={loading}
            >
              <span className="btn-icon">📱</span>
              Gerar Código PIX
            </button>
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Gerando código PIX...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {paymentData && paymentStatus !== 'PAID' && (
          <div className="payment-generated">
            <div className="pix-info">
              <h3>✨ PIX Gerado com Sucesso!</h3>
              <p className="instructions">
                {hasQrCodeData() || paymentData.copyAndPaste 
                  ? "Use o QR Code ou copie o código PIX para realizar o pagamento:"
                  : "Use o link de pagamento para realizar o PIX:"
                }
              </p>

              {/* Debug info - remove in production */}
              {/* {process.env.NODE_ENV === 'development' && (
                <div style={{ background: '#f0f0f0', padding: '10px', fontSize: '12px', marginBottom: '10px' }}>
                  <strong>Debug Info:</strong><br />
                  Method: {paymentData.method}<br />
                  Has QR Code Image: {hasQrCodeData() ? 'Yes' : 'No'}<br />
                  Has Copy/Paste: {paymentData.copyAndPaste ? 'Yes' : 'No'}<br />
                  Has Payment URL: {paymentData.paymentUrl ? 'Yes' : 'No'}<br />
                  QR Code Fields: {JSON.stringify({
                    qrCodeImage: !!paymentData.qrCodeImage,
                    qrCodeBase64: !!paymentData.qrCodeBase64,
                    qrCodeDataUrl: !!paymentData.qrCodeDataUrl,
                    qrcode: !!paymentData.qrcode,
                    qrCode: !!paymentData.qrCode
                  })}
                </div>
              )} */}

              {hasQrCodeData() ? (
                <div className="qr-code-container">
                  <img
                    src={getQrCodeImageSrc()}
                    alt="QR Code PIX"
                    className="qr-code"
                    onError={(e) => {
                      console.error('QR Code image failed to load:', {
                        src: e.target.src,
                        paymentData: paymentData
                      });
                      e.target.style.display = 'none';
                      // Show error message
                      const parent = e.target.parentNode;
                      if (parent && !parent.querySelector('.qr-error')) {
                        const errorDiv = document.createElement('div');
                        errorDiv.className = 'qr-error';
                        errorDiv.innerHTML = '<p>⚠️ Erro ao carregar QR Code</p><small>Use o código PIX abaixo ou o link de pagamento</small>';
                        parent.appendChild(errorDiv);
                      }
                    }}
                    onLoad={() => console.log('QR Code loaded successfully')}
                  />
                  <p className="qr-hint">Escaneie com o app do seu banco</p>
                </div>
              ) : (
                <div className="qr-code-placeholder">
                  <p>⚠️ QR Code não disponível</p>
                  <small>
                    {paymentData.copyAndPaste 
                      ? "Use o código PIX abaixo" 
                      : paymentData.paymentUrl 
                        ? "Use o link de pagamento abaixo"
                        : "Nenhum método de pagamento disponível"
                    }
                  </small>
                </div>
              )}

              {paymentData.copyAndPaste && (
                <div className="pix-key-container">
                  <label>Código PIX Copia e Cola:</label>
                  <div className="pix-key-wrapper">
                    <input
                      type="text"
                      value={paymentData.copyAndPaste}
                      readOnly
                      className="pix-key-input"
                      onClick={handleCopyPixKey}
                    />
                    <button
                      className={`btn-copy ${copied ? 'copied' : ''}`}
                      onClick={handleCopyPixKey}
                    >
                      {copied ? '✓ Copiado!' : '📋 Copiar'}
                    </button>
                  </div>
                </div>
              )}

              {paymentData.paymentUrl && (
                <div className="payment-url-container">
                  <label>Link de Pagamento:</label>
                  <div className="payment-url-actions">
                    <button className="btn-open-link" onClick={handleOpenPaymentLink}>
                      🔗 Abrir Link
                    </button>
                    <button
                      className={`btn-copy-url ${urlCopied ? 'copied' : ''}`}
                      onClick={handleCopyPaymentUrl}
                    >
                      {urlCopied ? '✓ Link Copiado!' : '📋 Copiar Link'}
                    </button>
                  </div>
                  <div className="payment-url-display">
                    <input
                      type="text"
                      value={paymentData.paymentUrl}
                      readOnly
                      className="payment-url-input"
                      onClick={handleCopyPaymentUrl}
                    />
                  </div>
                </div>
              )}

              <div className="payment-actions">
                <button 
                  className="btn-manual-check" 
                  onClick={handleManualCheck}
                  disabled={checkingStatus}
                >
                  {checkingStatus ? '⏳ Verificando...' : '🔄 Verificar Pagamento'}
                </button>
              </div>

              <div className="payment-expiry">
                <span className="expiry-icon">⏰</span>
                <span>Este código expira em 30 minutos</span>
              </div>
            </div>

            {checkingStatus && (
              <div className="status-checking">
                <div className="pulse-animation"></div>
                <p>Aguardando confirmação do pagamento...</p>
                <small>Após o pagamento, a confirmação é automática</small>
              </div>
            )}
          </div>
        )}

        {paymentStatus === 'PAID' && (
          <div className="payment-success">
            <div className="success-icon">✅</div>
            <h3>Pagamento Confirmado!</h3>
            <p>Seu pagamento foi processado com sucesso.</p>
            <p>Redirecionando para a análise...</p>
          </div>
        )}

        <div className="modal-footer">
          {paymentStatus !== 'PAID' && (
            <button className="btn-cancel" onClick={onClose}>
              {paymentData ? 'Fechar' : 'Cancelar'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PixPaymentModal;
